import React from 'react';
import ResponsiveECharts from './ResponsiveECharts';
import { baseOption, axeValeur } from './echartsTheme';

/**
 * Substitution de culture, en nuage de points.
 *
 *   axe X : écart de produit brut (PB actuel - PB de la culture de
 *           remplacement) — à droite, la substitution coûte du produit brut.
 *   axe Y : écart de risque (risque actuel - risque de la remplaçante) —
 *           en haut, la substitution fait gagner du risque évité.
 *
 * Chaque point porte le nom de la culture de remplacement.
 *
 * @param data [{ culture, ecartPB, ecartRisque }]
 */
export default function NuageSubstitutionChart({ data = [] }) {
  if (data.length === 0) {
    return <p className="tdb-chargement">Aucune culture de substitution.</p>;
  }

  // Les bornes encadrent les points avec une marge, pour que les quadrants
  // couvrent toute la surface du graphique.
  const pbs = data.map((d) => d.ecartPB);
  const risques = data.map((d) => d.ecartRisque);
  // Les quadrants débordent volontairement des points : ECharts découpe au
  // cadre, et l'axe n'a plus de bornes imposées.
  const etendre = (valeurs) => {
    const amplitude = Math.max(...valeurs) - Math.min(...valeurs) || 1;
    return {
      min: Math.min(0, ...valeurs) - amplitude,
      max: Math.max(0, ...valeurs) + amplitude,
    };
  };
  const { min: xMin, max: xMax } = etendre(pbs);
  const { min: yMin, max: yMax } = etendre(risques);

  const option = {
    ...baseOption(),
    legend: { show: false },
    // Marge droite juste suffisante pour le titre sur trois lignes : la zone
    // de tracé occupe tout le reste de la carte.
    grid: { left: 24, right: 110, bottom: 48, top: 72, containLabel: true },
    tooltip: {
      trigger: 'item',
      textStyle: { fontSize: 14, color: '#000000' },
      formatter: ({ data: point }) =>
        `<b>${point.nom}</b><br/>Écart de produit brut : ${point.value[0].toFixed(0)} €` +
        `<br/>Écart de la perte : ${point.value[1].toFixed(1)} pts`,
    },
    xAxis: {
      ...axeValeur({
        // Sur trois lignes : le titre tient dans la marge droite sans la
        // rendre plus large, la zone de tracé garde toute sa place.
        nom: 'Écart de\nproduit brut\n(en euros)',
        formatteur: (v) => Number(v).toFixed(1),
      }),
      // Posé au bout de l'axe et aligné à gauche : le texte part vers la
      // droite dans la marge libre, donc en dehors de la zone de tracé.
      nameLocation: 'end',
      nameRotate: 0,
      nameGap: 10,
      nameTextStyle: {
        fontSize: 14,
        color: '#000000',
        fontWeight: 600,
        align: 'left',
        verticalAlign: 'middle',
        lineHeight: 18,
      },
      splitLine: { lineStyle: { color: '#e2e5ea' } },
    },
    yAxis: axeValeur({
      nom: 'Écart de perte (points)',
      formatteur: (v) => Number(v).toFixed(1),
    }),
    series: [
      {
        type: 'scatter',
        symbolSize: 16,
        itemStyle: { color: '#5CA015' },
        label: {
          show: true,
          position: 'right',
          fontSize: 14,
          color: '#000000',
          formatter: ({ data: point }) => point.nom,
        },
        data: data.map((d) => ({
          nom: d.culture,
          value: [d.ecartPB, d.ecartRisque],
        })),
        // Les axes zéro servent de repère : à droite et en haut, la
        // substitution est gagnante sur les deux plans.
        markLine: {
          silent: true,
          symbol: 'none',
          lineStyle: { color: '#000000', type: 'dashed' },
          label: { show: false },
          data: [{ xAxis: 0 }, { yAxis: 0 }],
        },
        // Quadrants, selon le signe des deux écarts :
        //   risque < 0 et PB > 0  -> vert
        //   risque < 0 et PB < 0  -> jaune
        //   risque > 0 et PB < 0  -> rouge
        //   risque > 0 et PB > 0  -> orange
        markArea: {
          silent: true,
          data: [
            [{ xAxis: 0, yAxis: yMin, itemStyle: { color: 'rgba(146, 208, 80, 0.30)' } },
             { xAxis: xMax, yAxis: 0 }],
            [{ xAxis: xMin, yAxis: yMin, itemStyle: { color: 'rgba(255, 235, 132, 0.35)' } },
             { xAxis: 0, yAxis: 0 }],
            [{ xAxis: xMin, yAxis: 0, itemStyle: { color: 'rgba(230, 124, 115, 0.30)' } },
             { xAxis: 0, yAxis: yMax }],
            [{ xAxis: 0, yAxis: 0, itemStyle: { color: 'rgba(244, 177, 92, 0.30)' } },
             { xAxis: xMax, yAxis: yMax }],
          ],
        },
      },
    ],
  };

  // Rendu direct, sans div englobante : les autres graphiques font de même et
  // remplissent toute la largeur de la carte.
  return <ResponsiveECharts option={option} style={{ flex: '1 1 auto', minHeight: 460 }} />;
}
